/**
 * Template management section — download/upload/delete resume templates.
 * Displayed on the Resumes page above the resume list.
 */
import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Download,
  Upload,
  Trash2,
  AlertCircle,
  CheckCircle2,
  Loader2,
} from 'lucide-react';
import { templateApi } from '@/lib/api';
import { ApiError } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { showErrorToast, showSuccessToast } from '@/lib/toast';

export function TemplateSection() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Fetch template metadata
  const { data: template, isLoading } = useQuery({
    queryKey: ['template'],
    queryFn: templateApi.get,
  });

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      setUploadError(null);
      return templateApi.upload(file);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['template'] });
      showSuccessToast('Template uploaded successfully');
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    onError: (err) => {
      // Server-provided 400 errors land in ApiError.error (the validator's
      // multi-line description); ApiError.message is a generic fallback.
      const serverMessage =
        err instanceof ApiError ? err.error : null;
      const fullMessage =
        serverMessage ??
        (err instanceof Error ? err.message : 'Upload failed');
      setUploadError(fullMessage);
      // Toast only the first line so the popup stays compact; the inline
      // error block below renders the full message with formatting preserved.
      const firstLine = fullMessage.split('\n', 1)[0] ?? fullMessage;
      showErrorToast(firstLine);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: templateApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['template'] });
      showSuccessToast('Template deleted');
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : 'Delete failed';
      showErrorToast(message);
    },
  });

  // Download user template
  const downloadTemplate = async () => {
    try {
      await templateApi.download();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Download failed';
      showErrorToast(message);
    }
  };

  // Download default template
  const downloadDefault = async () => {
    try {
      await templateApi.downloadDefault();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Download failed';
      showErrorToast(message);
    }
  };

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.docx')) {
      setUploadError('Please select a .docx file');
      return;
    }

    uploadMutation.mutate(file);
  };

  if (isLoading) {
    return (
      <Card className="mb-6">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="text-base">Resume Template</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Template status */}
        {template ? (
          <div className="flex items-start justify-between gap-4 rounded-lg bg-green-50 p-4 text-sm">
            <div className="flex gap-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
              <div>
                <p className="font-semibold text-green-900">Custom template active</p>
                <p className="mt-0.5 text-green-700">{template.filename}</p>
                <p className="mt-1 text-xs text-green-600">
                  Uploaded {new Date(template.createdAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-start justify-between gap-4 rounded-lg bg-blue-50 p-4 text-sm">
            <div className="flex gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
              <div>
                <p className="font-semibold text-blue-900">Using default template</p>
                <p className="mt-0.5 text-blue-700">
                  Download and customize the default template, then upload your version.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-wrap gap-2">
          {/* Download default button (always shown) */}
          <Button
            size="sm"
            variant="outline"
            className="gap-2"
            onClick={downloadDefault}
          >
            <Download className="h-4 w-4" />
            Download Default
          </Button>

          {/* Download custom template button (if exists) */}
          {template && (
            <Button
              size="sm"
              variant="outline"
              className="gap-2"
              onClick={downloadTemplate}
            >
              <Download className="h-4 w-4" />
              Download Yours
            </Button>
          )}

          {/* Upload/Replace button */}
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".docx"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button
              size="sm"
              variant="outline"
              className="gap-2"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMutation.isPending}
            >
              {uploadMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Uploading…
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  {template ? 'Replace' : 'Upload'}
                </>
              )}
            </Button>
          </div>

          {/* Delete button (if exists) */}
          {template && (
            <Button
              size="sm"
              variant="outline"
              className="gap-2 text-destructive hover:text-destructive"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Deleting…
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4" />
                  Delete
                </>
              )}
            </Button>
          )}
        </div>

        {/* Upload error message — multi-line, preserves whitespace so the
            validator's step-by-step instructions remain readable. */}
        {uploadError && (
          <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive whitespace-pre-wrap font-mono leading-relaxed max-h-96 overflow-y-auto">
            {uploadError}
          </div>
        )}

        {/* Help text */}
        <div className="rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground">
          <p className="font-semibold text-foreground mb-1">How to use:</p>
          <ol className="space-y-1 list-decimal list-inside">
            <li>Download the template and open it in Microsoft Word</li>
            <li>
              Edit the template with your desired styling and layout
            </li>
            <li>
              Keep the placeholder text like{' '}
              <code className="bg-white px-1 py-0.5 rounded font-mono text-xs">
                {`{{HEADER_NAME}}`}
              </code>{' '}
              unchanged
            </li>
            <li>Save and upload your customized template</li>
            <li>Your generated resumes will use this template automatically</li>
          </ol>
        </div>
      </CardContent>
    </Card>
  );
}
